import eventBus from '~/event_bus';
import { i18n } from '~/modules/i18n';
import type { DialogOptions } from '~/types/dialog_options';
import Swal from 'sweetalert2';
import { RepeatingAction } from '~/types/recurring_options';
const { shift, ctrl } = useMagicKeys();
import { clearCoreErrors, handleCoreError } from '~/utils';

export function swalNotification(text: string, error = false, timeMS = 2000, onClick?: () => void) {
    const t = i18n.global.t;

    return Swal.fire({
        position: 'top-end',
        toast: true,
        showConfirmButton: false,
        timerProgressBar: true,
        title: error ? t('swal.error') : t('swal.success'),
        text,
        icon: error ? 'error' : 'success',
        timer: timeMS,
        didOpen: onClick
            ? (popup) => {
                popup.style.cursor = 'pointer';
                popup.addEventListener('click', () => {
                    Swal.close();
                    onClick();
                });
            }
            : undefined,
    });
}

export function swalUnknownError(timeMS = 2000) {
    const t = i18n.global.t;

    return swalNotification(
        t('basic.unknown_error'), // 'Er ging iets mis tijdens deze actie, probeer het nog eens.'
        true,
        timeMS,
    );
}

/**
 * Confirm before deletion
 *
 * @param {string} confirmMessage
 * @param {() => Promise<unknown>} onDelete
 */
export async function confirmDeletion(confirmMessage: string, onDelete?: () => any, successMessage?: string): Promise<boolean> {
    const t = i18n.global.t;
    const bypass = ctrl.value && shift.value;

    clearCoreErrors();

    const confirmed = bypass || await new Promise<boolean>((res) => eventBus.emit('modal.dialog.custom', {
        body: confirmMessage,
        cancel: t('basic.cancel'),
        confirm: t('basic.confirm_delete_short'),
        onConfirm: res,
    }));

    if (confirmed && onDelete) {
        try {
            await onDelete();
            if (successMessage) void swalNotification(successMessage);
        } catch (error) {
            handleCoreError(error);
        }
    }
    return confirmed;
}

export function scdBasic(message: string, titleKey?: string, acceptText?: string, declineText?: string, confirmColor?: string): Promise<boolean> {
    const t = i18n.global.t;
    return new Promise<boolean>((res) => {
        eventBus.emit('modal.dialog.custom', {
            title: t(titleKey ?? 'basic.confirm'),
            body: message,
            cancel: t(declineText ?? 'basic.cancel'),
            confirm: t(acceptText ?? 'basic.confirmation'),
            confirmColor,
            onConfirm: res,
        });
    });
}

export function scdCustomConfirmation(options: DialogOptions): Promise<boolean> {
    return new Promise<boolean>((res) => {
        eventBus.emit('modal.dialog.custom', { ...options, onConfirm: res });
    });
}

export function scdRadioButton<Values extends string>(
    title: string,
    message: string,
    options: { label: string; value: Values }[],
    cancel?: string,
    confirmButtonClass?: string,
): Promise<Values | undefined> {
    return new Promise((res) => {
        eventBus.emit('modal.dialog.radio_button', {
            title,
            message,
            options,
            cancel,
            confirmButtonClass,
            onConfirm: (value: string | number | undefined) => {
                res(value as Values | undefined);
            },
        });
    });
}

export async function scdRepeatingAction(deletion: boolean): Promise<RepeatingAction | undefined> {
    const t = i18n.global.t;
    const text_key = deletion ? 'delete' : 'edit';
    return await scdRadioButton(
        t(`tasks.${text_key}_repeating_title`),
        t(`tasks.${text_key}_repeating_message`),
        [
            { label: t(`tasks.${text_key}_current_and_future`), value: RepeatingAction.allInFuture },
            { label: t(`tasks.only_${text_key}_current`), value: RepeatingAction.selected },
        ],
        t('basic.cancel'),
    );
}
